// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// palette.ts — every colour that carries meaning, and an honest map of what each one can actually
// sit on.
//
// A sibling game in this fleet drew its walls at 1.14:1 against the board — in the right cells, at
// the right size, and completely invisible. Every geometry check passed, because a shape that is
// on-screen, correctly sized and the same colour as its background is geometrically perfect. On a
// deliberately cold, dark palette, invisible reads as ATMOSPHERE in a screenshot. So every colour
// here is held to WCAG 2.1 §1.4.11 (3:1, the floor for a non-text graphic) by tests/contrast.test.ts
// — against the surfaces it can REALLY sit on, which is what MARK_SURFACES is for. A blanket
// cross-product would be a lie in both directions: a jammed door only ever exists on a tile at snow
// depth 2 or more, and snow never falls on an indoor floor.
//
// TWO PAIRS ARE GENUINELY UNSATISFIABLE, AND BOTH ARE SOLVED WITH A SECOND CHANNEL RATHER THAN A
// LOWERED THRESHOLD:
//
// 1. ADJACENT SNOW DEPTHS. Each of the three depths clears 3:1 against open ground, but three tiers
//    inside one luminance range cannot also be 3:1 from EACH OTHER — the window is empty. Depth 3 is
//    the one that matters, because depth 3 is IMPASSABLE, and it carries a HATCH. The impassable
//    state is never colour-only. Adjacent tiers are asserted at >= 1.7, which is a clear lightness
//    step, and the test says so out loud rather than pretending.
//
// 2. INDOOR FLOOR vs OPEN GROUND. Pushing the floor tint to 3:1 against outdoor ground makes it so
//    light that five separate glyphs drawn ON it fall below 3:1 — measured, not guessed. The fix is
//    to notice that "indoors" is not actually the meaning: HEATED is. A room is enclosed by walls,
//    which are already at 5.1:1, so the enclosure is visible on its own; and every tile of a heated
//    room gets a `warmth` border at 8.1:1 against the floor. An indoor room with no warm border is
//    an unheated room — which is exactly the failure this game is about, so the absence of a signal
//    is the correct picture of it.

export const PALETTE = {
  /** The page behind everything. */
  night: '#0a0f15',
  /** Open outdoor ground — the surface most marks are measured against. */
  ground: '#1b2733',
  /** Indoor floor. A warm tint, not a contrast channel — see the header. */
  floor: '#33291f',
  /** HUD panels. */
  panel: '#111a23',
  /** Body text on a panel. */
  text: '#e8eef4',
  /** Secondary text on a panel. */
  dim: '#8fa3b5',

  /** The glyph outline that keeps a light mark legible on light snow. */
  ink: '#0a0f15',

  /** Snow, three depths. Depth 3 is impassable and additionally hatched. */
  snow1: '#66798c',
  snow2: '#9fb2c4',
  snow3: '#dfe9f2',
  /** The hatch drawn over depth 3 — the second channel for "you cannot walk here". */
  hatch: '#5a6a7a',

  /** Natural rock: impassable, and it is what makes the ridge a ridge. */
  rock: '#7d7268',
  /** Built wall. */
  wall: '#8d97a4',
  /** Built door. */
  door: '#d9a86a',
  /** A jammed door — only ever drawn on a tile at snow depth 2 or 3. */
  jam: '#8f1f16',
  /** Built path: half snow accumulation. */
  path: '#9a8f7f',

  /** A stand of timber. */
  stand: '#4fa268',
  /** A forage patch. */
  forage: '#a99bea',

  /** Stockpile — the larder itself. */
  store: '#e0a53c',
  /** Stove: meals, and remedies, out of the same building. */
  stove: '#ff8b4a',
  /** Firepit. */
  pit: '#ff5f4a',
  /** Bed. */
  bed: '#79c7d8',
  /** The border drawn on every tile of a heated room. */
  warmth: '#f0b25c',

  /** A colonist. */
  person: '#f4ecdc',
  /** A designation the player has placed and nobody has done yet. */
  desig: '#ffe066',

  /** Good and bad, on a panel. Blue-orange-ish rather than red-green wherever it can be. */
  good: '#68d391',
  bad: '#ff6b5c',
} as const;

export type Mark = keyof typeof PALETTE;

/** The surfaces something can be drawn on. */
export const SURFACES = ['ground', 'floor', 'panel', 'snow2', 'snow3'] as const;
export type Surface = (typeof SURFACES)[number];

/**
 * What can sit on what, honestly. This is the whole value of the contrast gate: a blanket
 * cross-product would demand impossible pairs and permit missing ones.
 */
export const MARK_SURFACES: Record<string, readonly Surface[]> = {
  // Snow falls outdoors, on open ground. It never falls on an indoor floor, by rule.
  snow1: ['ground'],
  snow2: ['ground'],
  snow3: ['ground'],
  // The hatch's whole job is to be visible on the depth it marks.
  hatch: ['snow3'],
  // The outline exists to separate a light glyph from light snow.
  ink: ['snow2', 'snow3'],

  // Terrain. None of these can ever be indoors.
  rock: ['ground'],
  stand: ['ground'],
  forage: ['ground'],

  // Built things. A wall or a door bounds a room, so it is drawn against both surfaces.
  wall: ['ground', 'floor'],
  door: ['ground', 'floor'],
  path: ['ground', 'floor'],
  store: ['ground', 'floor'],
  stove: ['ground', 'floor'],
  pit: ['ground', 'floor'],
  bed: ['ground', 'floor'],
  warmth: ['ground', 'floor'],

  // A jammed door exists ONLY on a tile at snow depth >= 2. That is the rule that creates it.
  jam: ['snow2', 'snow3'],

  // People and the player's marks.
  person: ['ground', 'floor'],
  desig: ['ground', 'floor'],

  // Panel furniture.
  text: ['panel'],
  dim: ['panel'],
  good: ['panel'],
  bad: ['panel'],
};

/** Every mark the contrast test walks. */
export const MARKS: readonly string[] = Object.keys(MARK_SURFACES);

/**
 * The four resources, in the fixed order the HUD and the ledger both use. Colours are reused from
 * the buildings that hold them so a player learns one association, not two.
 */
export const RESOURCES = ['wood', 'food', 'meal', 'remedy'] as const;
export type Resource = (typeof RESOURCES)[number];

export const RESOURCE_COLOUR: Record<Resource, string> = {
  wood: PALETTE.stand,
  food: PALETTE.forage,
  meal: PALETTE.store,
  remedy: PALETTE.bed,
};

/**
 * A glyph per built thing and per terrain. THE SHAPE IS THE CHANNEL THAT SURVIVES EVERYTHING —
 * greyscale, dichromacy, a 28px cell and a cracked screen. Every one of these is a distinct
 * silhouette, asserted in the contrast test rather than left to the renderer's manners.
 */
export const GLYPH = {
  stand: '/\\',
  forage: '~',
  rock: '▲',
  wall: '█',
  door: '┐',
  path: '··',
  store: '▤',
  stove: '◉',
  pit: '✵',
  bed: '▬',
} as const;
